<template>
  <div class="admin-header">
    <h2>Educator Data</h2>
  </div>
  <div class="educator-data-card">
    <div class="toolbar">
      <div class="search-wrapper">
        <div class="search-input">
          <SearchIcon class="search-icon" />
          <input
            v-model="searchQuery"
            type="text"
            placeholder="Search by email..."
            class="search-field"
          />
        </div>
        <button class="search-button" @click="triggerSearch">Search</button>
      </div>
      <ActionButton
        variant="primary"
        size="large"
        :loading="exporting"
        loading-text="Exporting..."
        @click="handleExportCSV"
      >
        <template #icon-left>
          <img
            src="/images/icon/download-icon.svg"
            alt="download"
            class="export-icon"
          />
        </template>
        Export CSV
      </ActionButton>
    </div>

    <TableData
      :headers="tableHeaders"
      :items="items"
      :total-pages="totalPages"
      :current-page="currentPage"
      :loading="loading"
      variant="rounded"
      @update:current-page="handlePageChange"
    />
  </div>
</template>

<script setup>
import { ref, onMounted, watch } from "vue";
import dayjs from "dayjs";
import TableData from "@/components/common/TableData.vue";
import ActionButton from "@/components/common/ActionButton.vue";
import { SearchIcon } from "@/components/icons";
import sfiService from "@/services/sfiService";

const tableHeaders = [
  { text: "Email", value: "email", sortable: true },
  { text: "Last Log In Time", value: "last_login", sortable: true },
  { text: "Assignments", value: "assignments", sortable: true },
  { text: "Sessions", value: "sessions", sortable: true },
  { text: "Students", value: "students", sortable: true },
];

const searchQuery = ref("");
const currentPage = ref(1);
const itemsPerPage = 4;
const totalPages = ref(1);
const items = ref([]);
const loading = ref(false);

const formatLastLogin = (value) => {
  if (!value) return "-";
  const date = dayjs(value);
  return date.isValid() ? date.format("MMM D, YYYY h:mm A") : "-";
};

const mapEducator = (educator, index) => ({
  id: educator.id ?? educator.educator_id ?? index,
  email: educator.email ?? "-",
  last_login: formatLastLogin(
    educator.last_login ?? educator.lastLogin ?? educator.last_login_at
  ),
  assignments: educator.assignments ?? educator.total_assignments ?? 0,
  sessions: educator.sessions ?? educator.total_sessions ?? 0,
  students: educator.students ?? educator.total_students ?? 0,
});

const fetchEducators = async () => {
  loading.value = true;
  try {
    const response = await sfiService.getEducators({
      page: currentPage.value,
      limit: itemsPerPage,
      orderBy: "name",
      orderDir: "desc",
      search: searchQuery.value.trim(),
    });

    const payload = response?.data?.data ?? response?.data ?? {};
    const list =
      payload.educators ?? payload.items ?? payload.data ?? payload.rows ?? [];
    const pagination =
      payload.pagination ?? payload.meta ?? response?.data?.meta ?? {};

    items.value = list.map(mapEducator);

    const totalPagesFromApi =
      pagination.totalPages ??
      pagination.total_pages ??
      pagination.lastPage ??
      pagination.last_page;
    const totalItems =
      pagination.total ?? pagination.totalItems ?? pagination.total_items;

    if (totalPagesFromApi) {
      totalPages.value = Math.max(1, Number(totalPagesFromApi));
    } else if (totalItems !== undefined) {
      totalPages.value = Math.max(
        1,
        Math.ceil(Number(totalItems) / itemsPerPage)
      );
    } else {
      totalPages.value =
        list.length < itemsPerPage ? currentPage.value : currentPage.value + 1;
    }
  } catch (error) {
    console.error("Error fetching educators:", error);
    items.value = [];
    totalPages.value = 1;
  } finally {
    loading.value = false;
  }
};

const handlePageChange = (page) => {
  currentPage.value = page;
  fetchEducators();
};

let searchDebounceTimer = null;
watch(searchQuery, () => {
  if (searchDebounceTimer) clearTimeout(searchDebounceTimer);
  searchDebounceTimer = setTimeout(() => {
    currentPage.value = 1;
    fetchEducators();
  }, 400);
});

const triggerSearch = () => {
  if (searchQuery.value.trim() === "") return;
  if (searchDebounceTimer) clearTimeout(searchDebounceTimer);
  currentPage.value = 1;
  fetchEducators();
};

const exporting = ref(false);

const handleExportCSV = async () => {
  if (exporting.value) return;
  exporting.value = true;
  try {
    const response = await sfiService.exportEducatorsCsv({
      search: searchQuery.value.trim(),
    });

    const blob = new Blob([response.data], {
      type: response.headers?.["content-type"] || "text/csv",
    });
    const url = window.URL.createObjectURL(blob);

    const contentDisposition = response.headers?.["content-disposition"];
    const today = dayjs().format("YYYY-MM-DD");
    const filename = contentDisposition
      ? contentDisposition.split("filename=")[1].replace(/"/g, "")
      : `sfi-educators-${today}.csv`;

    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();

    window.URL.revokeObjectURL(url);
    document.body.removeChild(link);
  } catch (error) {
    console.error("Error exporting educators CSV:", error);
    alert("Failed to export CSV. Please try again.");
  } finally {
    exporting.value = false;
  }
};

onMounted(() => {
  fetchEducators();
});
</script>

<style scoped>
.educator-data-card {
  background: #ffffff;
  border-radius: 12px;
  margin-top: 16px;
}

.toolbar {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 24px;
}

.search-wrapper {
  flex: 1;
  display: flex;
  align-items: stretch;
  min-width: 0;
}

.search-input {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 16px;
  height: 56px;
  padding: 4px 16px;
  background: #ffffff;
  border: 1px solid #6c737f;
  border-right: none;
  border-top-left-radius: 8px;
  border-bottom-left-radius: 8px;
  min-width: 0;
}

.search-icon {
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  opacity: 0.6;
}

.search-field {
  flex: 1;
  min-width: 0;
  border: none;
  outline: none;
  background: transparent;
  font-family: "League Spartan", sans-serif;
  font-size: var(--fs-big);
  color: #3b4648;
}

.search-field::placeholder {
  color: #6c737f;
  font-weight: 400;
}

.search-button {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 56px;
  padding: 10px 20px;
  background: #00599a;
  color: #ffffff;
  border: none;
  border-top-right-radius: 8px;
  border-bottom-right-radius: 8px;
  font-family: "League Spartan", sans-serif;
  font-size: var(--fs-big);
  font-weight: 500;
  cursor: pointer;
}

.search-button:hover {
  background: #004d87;
}

.export-icon {
  width: 24px;
  height: 24px;
}

@media (max-width: 768px) {
  .educator-data-card {
    padding: 20px;
  }

  .toolbar {
    flex-direction: column;
    align-items: stretch;
  }
}
</style>
